/**
 * Quiff hairstyles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_named_haircuts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const quiffHairstylesPack: ToilePack = {
    slug: 'quiff-hairstyles',
    category: 'beauty',
    name: 'Quiff hairstyles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'classic', label: 'Classic quiff', subject: 'a tall classic quiff lifted at the front and swept back over short sides'},
        {key: 'textured', label: 'Textured quiff', subject: 'a textured matte quiff with piecey volume'},
        {key: 'side', label: 'Side quiff', subject: 'a quiff swept to one side with a hard part line'},
    ],
    styles: [
        {key: 'front', label: 'Front view', style: 'a clean barber lookbook portrait against a plain pale-grey wall, soft daylight from camera-front and a little above, a bright even balance with a faint shadow under the fringe', palette: 'Pale grey and white, a white Oxford shirt open at the collar', framing: 'head and shoulders, centred, facing the camera'},
        {key: 'profile', label: 'Profile', style: 'a barber lookbook profile against a plain pale-grey wall showing the height of the front, daylight from camera-left, a moderate balance with soft shade behind the ear', palette: 'Pale grey and white, a navy knit at the collar', framing: 'head turned in full profile, eye-level'},
        {key: 'evening', label: 'Night out', style: 'an evening portrait in a cocktail bar, a warm amber glow from camera-right, a dark balance with the face and the hair clearly lit against a dark room', palette: 'Amber, walnut and deep green, a dark shirt open at the collar, the bar blurred behind', framing: 'chest-up, three-quarter turn'},
    ],
    directions: ['grey hair', 'with a beard', 'curly hair'],
    directionHint: 'try: grey hair',
    seo: {
        title: 'Quiff Hairstyles: Classic, Textured, Side',
        metaDescription: 'Nine pictures of the quiff, the front lifted up and swept back: a classic, a textured and a side quiff, from the front, in profile and at night.',
        h1: 'Quiff hairstyles: classic, textured, side',
        tagline: 'Nine pictures, three quiffs, each from the front, in profile and on a night out.',
        intro: 'A quiff is the hair at the front, lifted up and swept back, usually over short sides. These pictures show a tall classic quiff, a textured matte quiff with piecey volume, and a side quiff swept to one side with a hard part. Each is seen from the front, in profile and at night, so the height and how it holds are visible. You can type a change, such as a beard, to ask for the cut with it.',
        useCases: [
            'a reference for the height at the front',
            'choosing a classic, textured or side quiff',
            'a barbershop wall print',
            'a profile picture',
        ],
        faq: [
            {q: 'How is the textured quiff different from the classic?', a: 'The textured quiff has a matte finish and piecey volume. The classic is tall and swept back.'},
            {q: 'What is a side quiff?', a: 'The hair is swept to one side with a hard part line, rather than straight back.'},
            {q: 'Can I change the colour or add a beard?', a: 'Yes. Type the change and the picture is made with it. It is a request, so the result may differ from the sample.'},
        ],
    },
}
